// import logger from 'logger_module';
import { ZuulAuthClient } from './zuul_auth_client';
export interface OauthConfig {
    GSID: string;
    accountId?: number;
    accessToken: string;
    dc: string;
    refreshTime?: number;
}


/**
 * Responsible to preform all the Authentication related functionalities.
 * 1. Checking the current access token validity time before each API call
 * 2. Maintains Authentication config 
 * 3. Provides a refreshed token if expires, or less then the threshold time limit
 * @export
 * @class OauthClient
 */
export class OauthClient {
    

    static getOauthConfig() {
        return this.oauthConfig;
    }

    static setOauthConfig(oauthInfo: OauthConfig, clientRefreshTokenFunc : any, libraryServiceUrl: string, oauthStartTime: number = 0,redirectToLoginCallback: any) {
        if(!oauthInfo.accessToken || oauthInfo.accessToken.length<=0) {
            throw new Error('no accessToken present');
            //return;
        }
        // In Zuul mode GSID is metadata-only (auth is fully managed by Zuul); skip the strict
        // GSID validation so bootstrapAuth can seed OauthClient even when the GSID cookie is
        // absent (e.g. first-time guest sessions before the cookie is written).  The legacy
        // non-Zuul path keeps the original strict check unchanged.
        if(!ZuulAuthClient.isEnabled() && (!oauthInfo.GSID || oauthInfo.GSID.length <=0)) {
            throw new Error('no GSID present');
            //return;
        }
        OauthClient.oauthConfig = oauthInfo;
        this.clientRefreshTokenFunc = clientRefreshTokenFunc;
        this.redirectToLoginFunc = redirectToLoginCallback;
        this.libraryServiceUrl = libraryServiceUrl;
        this.setStartTime(oauthStartTime);
        // photoOrg.refreshTokenTime is kept dynamically accurate by refreshSuccess (legacy and
        // this class both write to it), so it's safe to reseed from it on every call here -
        // unlike oauthInfo.refreshTime, which is only the static value from initial login.
        //@ts-ignore
        this.setRefreshTokenTime(photoOrg.refreshTokenTime);
    }

    static checkAndGetRefreshToken(baseUrl:string,noodle: string, backFunction: () => void = () => {}) {
        // In Zuul mode this same diffTime check still runs, but photoOrg.refreshTokenTime/
        // photoOrg.startTime are maintained by zuul_auth_client.js's
        // populateOauthInfoFromPayload() (token exp/iat) instead of this class's own
        // refresh flow - once it trips, fetchToken() below routes to
        // ZuulAuthClient.refreshIdToken() (getIdToken()) instead of /refresh_access_token.
        try {
            const endTime = new Date().getTime();
            const diffTime = (endTime - this.startTime) - this.refreshTokenTime;
            const logDetails = ' diffTime=' + diffTime + ' endTime=' + endTime + ' startTime=' + this.startTime +
                ' refreshTokenTime=' + this.refreshTokenTime + ' isRefreshRequestSent=' + this.isRefreshRequestSent;

            if (diffTime >= 0) {
                if (!this.isRefreshRequestSent) {
                    //@ts-ignore
                    photoOrg.clientLogger && photoOrg.clientLogger.info({ description: 'OAuthFetcher:checkAndGetRefreshToken calling fetchToken"' + logDetails });
                    this.fetchToken(baseUrl, noodle, backFunction,this.redirectToLoginFunc);
                } else {
                    // a refresh is already in flight - queue this caller's continuation instead
                    // of letting it proceed with the still-expired token; it runs once the
                    // in-flight refresh settles (success or failure), via flushPendingRefreshCallbacks.
                    this.pendingRefreshCallbacks.push(backFunction);
                }
            } else {
                // token not expired - run the caller's continuation immediately.
                backFunction();
            }
        } catch (e) {
            //@ts-ignore
            photoOrg.clientLogger && photoOrg.clientLogger.error({
                description: 'LIBRARY:OauthClient:checkAndGetRefreshToken', errorCode: 'LIBRARY:OauthClient', err_desc: e, err_stack: e.stack
            });
        }
    }

   

    static checkOauthAvailable() {
        if (ZuulAuthClient.isEnabled()) {
            // Auth is resolved via Zuul during page bootstrap; skip the legacy synchronous
            // /getOauthInfo fallback used by callers of this method.
            return true;
        }
        try {
            let oauthInfoAvailable = false;
            if ( this.getAccessToken() && this.getGSID()) {
                oauthInfoAvailable = true;
            } else {
                console.log('from oauthfetcher');
                this.getAjaxObj({
                    url: this.libraryServiceUrl + '/getOauthInfo',
                    dataType: 'json',
                    type: 'GET',
                        async: false,
                        headers: this.getOauthHeaders()
                }).done( (data: any, textStatus: string, jqXHR: any) => {
                    if (data.oauthInfo) {
                        this.oauthConfig = {
                            ...data.oauthInfo,
                            accessToken: data.oauthInfo.accessToken || data.oauthInfo.oa2
                        };
                        oauthInfoAvailable = !!(this.oauthConfig.accessToken && this.oauthConfig.GSID);
                        this.setRefreshTokenTime(data.oauthInfo.refreshTime*1000);
                    }
                    return oauthInfoAvailable;//for testcase
                }).fail((data: any, textStatus: string, jqXHR: any) => {
                    oauthInfoAvailable = false;
                    return oauthInfoAvailable;//for testcase
                });
            }
            return oauthInfoAvailable;

        } catch (e) {
            //@ts-ignore
            photoOrg.clientLogger && photoOrg.clientLogger.error({
                description: 'LIBRARY:OauthClient:checkOauthAvailable', errorCode: 'LIBRARY:OauthClient', err_desc: e, err_stack: e.stack
            });
        }
    }

    static fetchToken(baseUrl: string, noodle: string, callback: () => void, failureCallback= (removeCookie: boolean,redirectToLogin: boolean) => {}) {
        try {
            if (ZuulAuthClient.isEnabled()) {
                // Zuul mode: force a fresh idToken via the SDK instead of the legacy
                // /refresh_access_token call. zuul_auth_client.js's populateOauthInfoFromPayload()
                // already updated the photoOrg.startTime/photoOrg.refreshTokenTime globals for
                // this fetch - mirror them onto this class's own statics so the next
                // checkAndGetRefreshToken() call measures freshness from here, not the old value.
                // Mirrors the legacy branch's coalescing/callback contract below exactly: only
                // one SDK call in flight at a time, and the caller's continuation (and any queued
                // checkAndGetRefreshToken() callers) always fires - success or failure - the same
                // way refreshSuccess()'s failure branch always calls both failureCallback and
                // callback, so a caller waiting on this (e.g. AjaxHelper.send()) is never left
                // permanently unresolved.
                const backFunction = (callback === undefined) ? () => {} : callback;
                if (!OauthClient.isRefreshRequestSent) {
                    OauthClient.isRefreshRequestSent = true;
                    //@ts-ignore
                    photoOrg.clientLogger && photoOrg.clientLogger.info({ description: 'OauthClient:fetchToken - Zuul mode, forcing idToken refresh', errorCode: 'LIBRARY:ZUUL_STATUS', Noodle: noodle, quickLogger: true });
                    ZuulAuthClient.refreshIdToken().then((token) => {
                        OauthClient.isRefreshRequestSent = false;
                        if (token) {
                            //@ts-ignore
                            if (typeof photoOrg !== 'undefined') {
                                //@ts-ignore
                                this.startTime = photoOrg.startTime;
                                //@ts-ignore
                                this.refreshTokenTime = photoOrg.refreshTokenTime;
                            }
                            backFunction();
                            this.flushPendingRefreshCallbacks();
                        } else {
                            //@ts-ignore
                            photoOrg.clientLogger && photoOrg.clientLogger.error({ description: 'OauthClient:fetchToken - Zuul mode forced refresh did not return a token', errorCode: 'LIBRARY:ZUUL_STATUS', Noodle: noodle });
                            failureCallback(false, true);
                            backFunction();
                            this.flushPendingRefreshCallbacks();
                        }
                    });
                }
                return;
            }
            console.log('fetch token call');
            this.setBaseUrl(baseUrl);
            
            const backFunction = (callback === undefined) ? () => { console.log('backFunction'); } : callback;
            if (!OauthClient.isRefreshRequestSent) {
                OauthClient.isRefreshRequestSent = true;
                //@ts-ignore
                photoOrg.clientLogger && photoOrg.clientLogger.info({ 'description': 'fetchToken:: Before getting new refresh token', 'OLD-accesstoken': this.oauthConfig.accessToken, 'Noodle': noodle });
                this.getRefreshToken(noodle, backFunction, failureCallback);
            }
        } catch (e) {
            //@ts-ignore
            photoOrg.clientLogger && photoOrg.clientLogger.error({
                description: 'LIBRARY:OauthClient:fetchToken', errorCode: 'LIBRARY:OauthClient', err_desc: e, err_stack: e.stack
            });
        }
    }

    /**
     * getting new access token from builder
     * @param response
     */
    static updateAccesstokenFromClient(response: any, callback: any) {
        if (response && response.accesstoken) {
            this.setAccesstoken(response.accesstoken);
        } else {
            //@ts-ignore
            photoOrg.clientLogger && photoOrg.clientLogger.error({ description: 'RefreshSuccess::updateOa2FromBuilder, No oauth token available in builder response ' });
        }
        try {
            // Always invoke the original caller's continuation - otherwise a missing/invalid
            // builder response leaves AjaxHelper.send()'s promise (or any other caller waiting
            // on this callback) hanging forever. Guarded separately from the reset/flush below -
            // a throwing (or non-function) callback must never prevent isRefreshRequestSent from
            // clearing or queued callers from being released.
            if (typeof callback === 'function') {
                callback();
            }
        } catch (e) {
            //@ts-ignore
            photoOrg.clientLogger && photoOrg.clientLogger.error({
                description: 'LIBRARY:OauthClient:updateAccesstokenFromClient', errorCode: 'LIBRARY:OauthClient', err_desc: e
            });
        }
        this.isRefreshRequestSent = false;
        this.flushPendingRefreshCallbacks();
    }

    private static oauthConfig: OauthConfig;
    static isRefreshRequestSent: boolean = false;
    // Callers that hit checkAndGetRefreshToken while a refresh is already in flight get queued
    // here instead of proceeding immediately with the still-expired token; they're all invoked
    // once the in-flight refresh settles (success or failure).
    private static pendingRefreshCallbacks: Array<() => void> = [];
    private static refreshTokenTime = 12600000; // 3.5hours
    private static startTime = 0;
    private static initiateFetchTokenOnFailure: number = 0;
    private static refreshTokenTimeOutId: any = 0;
    private static baseUrl: string;
    static clientRefreshTokenFunc: any;
    static redirectToLoginFunc: any;
    private static libraryServiceUrl: string = '';

    private static resetRefreshTokenTime() {
        this.refreshTokenTime = 12600000; // 3.5hours
    }

    /**
     * Invokes and clears all queued callbacks from checkAndGetRefreshToken calls that arrived
     * while a refresh was already in flight.
     */
    private static flushPendingRefreshCallbacks() {
        const queued = this.pendingRefreshCallbacks;
        this.pendingRefreshCallbacks = [];
        queued.forEach((queuedCallback) => {
            try {
                queuedCallback();
            } catch (e) {
                //@ts-ignore
                photoOrg.clientLogger && photoOrg.clientLogger.error({
                    description: 'LIBRARY:OauthClient:flushPendingRefreshCallbacks', errorCode: 'LIBRARY:OauthClient', err_desc: e
                });
            }
        });
    }

    static getclientRefreshTokenFunc():any{
        return this.clientRefreshTokenFunc
    }

    static getRefereshTokenTime():number{
        return this.refreshTokenTime
    }

    static setAccesstoken(accesstoken: string) {
        if (accesstoken) {
            this.oauthConfig.accessToken = accesstoken;
            // Keep the shared global in sync too - oauthFetcher.js's refreshSuccess already does
            // this (photoOrg.oauthInfo.oa2 = ...), and other code (getUserInfo/updateSession's
            // Apigee headers, getBrowserSessionCookie('oa2')) reads photoOrg.oauthInfo directly
            // rather than through this class, so it needs the freshly refreshed token too.
            //@ts-ignore
            if (typeof photoOrg !== 'undefined' && photoOrg.oauthInfo) {
                //@ts-ignore
                photoOrg.oauthInfo.oa2 = accesstoken;
            }
        }
    }

    static getAccessToken():string{
        return this.oauthConfig.accessToken;
    }
    
    static getGSID(): string {
        return this.oauthConfig.GSID
    }

    private static getOauthHeaders(): any {
        const headers: any = {};
        if (this.oauthConfig && this.oauthConfig.accessToken) {
            // Route through ZuulAuthClient.getAuthHeaderValue() so the Bearer/OAuth prefix
            // decision is made in the one place zuul_auth_client.js owns it - this class was
            // seeded with the Zuul idToken as accessToken (see bootstrapAuth's setOauthConfig
            // call) but was still hardcoding the legacy "OAuth " prefix here, which is why
            // Projects-page API calls kept sending "OAuth <jwt>" instead of "Bearer <jwt>"
            // even once Zuul was enabled. The "OAuth " fallback only matters if isEnabled()
            // is somehow true here yet zuulAuthClient's own idToken is unset - a race that
            // shouldn't happen since both are seeded from the same token, but keeps this safe.
            const authHeaderValue = ZuulAuthClient.getAuthHeaderValue(this.oauthConfig.accessToken)
                || ('OAuth ' + this.oauthConfig.accessToken);
            headers.Authorization = authHeaderValue;
            headers.access_token = authHeaderValue;
            const acctIdHeaderValue = ZuulAuthClient.getAcctIdHeaderValue();
            if (acctIdHeaderValue) {
                headers.AcctId = acctIdHeaderValue;
            }
        }
        if (this.oauthConfig && this.oauthConfig.GSID) {
            headers.GSID = this.oauthConfig.GSID;
        }
        return headers;
    }

    static setRefreshTokenTime(refreshTokenTime: number) {
        if (refreshTokenTime) {
            this.refreshTokenTime = refreshTokenTime;
        }
    }
    static setStartTime(startTime: number) {
        if (startTime) {
            this.startTime = startTime;
        }
    }
    private static setBaseUrl(baseUrl: string) {
        if (baseUrl) {
            this.baseUrl = baseUrl;
        }
    }

    /**
     * This below will do ajax for to the server to get the new oauth token.
     */
    private static getRefreshToken(noodle: string, callback: () => void,
                                   failureCallback: (removeCookie: boolean,redirectToLogin: boolean) => void) {
        try {
            const defaultParams = {
                url: this.baseUrl + '/refresh_access_token',
                // url: '/refresh_access_token',
                type: 'GET',
                dataType: 'json',
                async: false,
                cache: false,
                force: false,
                headers: {
                    ...this.getOauthHeaders(),
                    Noodle: noodle
                }
            };            
            defaultParams.url = defaultParams.url + '?force=true';
            // Extend the defaultParams with anything passed in by the user
            // Parameters set by user will overwrite defaults on collisions
            console.log('jquery ajax call for refreshtoken');

            this.getAjaxObj(defaultParams).done((data:any) => {
                this.refreshSuccess(data, noodle, callback, failureCallback);
            }).fail((jqXHR:any) => {
                const responsetext = jqXHR.responseText || '';
                //@ts-ignore
                photoOrg.clientLogger && photoOrg.clientLogger.warn({
                        description: 'description = refresh_access_token failed',
                    responsetext, Noodle: noodle,
                    initiateFetchTokenOnFailure: this.initiateFetchTokenOnFailure,
                    quickLogger: true
                });

                this.setRefreshTokenTime(60 * 1000);
                this.setStartTime(new Date().getTime());
                this.isRefreshRequestSent = false;
                clearTimeout(this.refreshTokenTimeOutId);
                // we should trigger fetch token call on failure at max of 3 times
                if (this.initiateFetchTokenOnFailure < 3) {
                    this.setTimeOutForRefreshToken(noodle, callback, failureCallback);
                    this.initiateFetchTokenOnFailure++;
                }
                failureCallback(false,false);
                // Also trigger the caller's continuation (e.g. AjaxHelper.send()'s pending
                // promise) so a hard network failure on /refresh_access_token doesn't leave
                // it hanging forever - it only ever waits on this callback, not failureCallback.
                callback();
                this.flushPendingRefreshCallbacks();
            });
        } catch (e) {
            //@ts-ignore
            photoOrg.clientLogger && photoOrg.clientLogger.error({
                description: 'LIBRARY:OauthClient:getRefreshToken', errorCode: 'LIBRARY:OauthClient', err_desc: e, err_stack: e.stack
            });
        }
    }

    public static getAjaxObj(defaultParams:any):any{
        return $.ajax(defaultParams);
    }
    /**
     *
     * @param data
     * date json response from server.
     * This will take care of updating the oauth information,update the refresh token time and page start time to current time.
     * if we get any error we do redirect to the store logic page.
     * Error may be:
     * {:error => 'Unable to get Access Token', :redirectToLogin=>true}
     * {:error => 'Cannot find the refresh token in the session and accessTokenFromCSR is true',:removeCookie => true,:redirectToLogin=>true}
     * {:error => 'Cannot find the refresh token in the session',:redirectToLogin=>true}
     *
     */
    private static refreshSuccess(data: any, noodle: string, callback: () => void, failureCallback: (removeCookie: boolean,redirectToLogin: boolean) => void) {
        try {
            this.initiateFetchTokenOnFailure = 0;
            // alert('refreshing');
            if (data && data.access_token !== undefined) {
                this.setAccesstoken(data.access_token);

                this.setStartTime(new Date().getTime());
                //@ts-ignore
                photoOrg.clientLogger && photoOrg.clientLogger.info({ 'description': 'RefreshSuccess:: Got new refresh token', 'NEW-oa2': this.getOauthConfig().accessToken, 'Noodle': noodle });
                if (data.access_token_expires_in) {
                    this.setRefreshTokenTime(data.access_token_expires_in * 1000);
                } else {
                    this.resetRefreshTokenTime();
                }
                //@ts-ignore
                if (typeof photoOrg !== 'undefined') {
                    //@ts-ignore
                    photoOrg.startTime = this.startTime;
                    //@ts-ignore
                    photoOrg.refreshTokenTime = this.refreshTokenTime;
                }
                //@ts-ignore
                if (typeof photoOrg !== 'undefined' && photoOrg.oauthInfo) {
                    //@ts-ignore
                    photoOrg.oauthInfo.oa2 = data.access_token;
                    //@ts-ignore
                    photoOrg.oauthInfo.refreshTime  = this.refreshTokenTime/1000;
                }
                this.isRefreshRequestSent = false;
                callback();
                this.flushPendingRefreshCallbacks();
            } else {
                this.resetRefreshTokenTime();
                if (data.removeCookie || data.redirectToLogin) {
                    // triggering builder callback incase of failure in getting accesstoken from global session for all contexts(also for imemories)
                    // photoOrg.photoPicker.config.callbackObj.getNewAccessToken - builder callback for imemories
                    // added photoOrg.photoLib condition check as it is not available in uploader
                    // picker check (.loadAsPhotoPicker) as callbackObj is not present in library
                    const clientRefresTokenFunc = this.clientRefreshTokenFunc;
                    if (clientRefresTokenFunc) {
                        //@ts-ignore
                        photoOrg.clientLogger && photoOrg.clientLogger.warn({
                            description: 'RefreshSuccess:: Not able to get new refresh token, so triggering builder callback to fetch new token',
                            token_failure_response: JSON.stringify(data), Noodle: noodle
                        });
                        // Deliberately leaving isRefreshRequestSent as true here - the refresh isn't
                        // actually resolved yet (the builder call is async), so any other caller
                        // that hits checkAndGetRefreshToken in the meantime queues behind it rather
                        // than proceeding with the stale token or starting its own duplicate
                        // refresh. Wrapping the builder callback so the original caller's
                        // `callback` (closure-captured here, not a shared slot) actually reaches
                        // updateAccesstokenFromClient - it's reset and the queue flushed there,
                        // once the builder genuinely resolves.
                        clientRefresTokenFunc((response: any) => {
                            this.updateAccesstokenFromClient(response, callback);
                        });
                        return;
                    } else {
                        //@ts-ignore
                        photoOrg.clientLogger && photoOrg.clientLogger.warn({
                            description: 'RefreshSuccess:: Not able to get new refresh token',
                            token_failure_response: JSON.stringify(data), Noodle: noodle
                        });
                        // ********* redirectToLoginPage *******
                        failureCallback(data.removeCookie,data.redirectToLogin);
                        this.isRefreshRequestSent = false;
                        callback();
                        this.flushPendingRefreshCallbacks();
                        return;
                    }
                }
                this.isRefreshRequestSent = false;
                callback();
                this.flushPendingRefreshCallbacks();
            }
        } catch (e) {
            //@ts-ignore
            photoOrg.clientLogger && photoOrg.clientLogger.error({
                description: 'LIBRARY:OauthClient:refreshSuccess', errorCode: 'LIBRARY:OauthClient', err_desc: e, err_stack: e.stack
            });
        }
    }

    static getDiffTime() {
        return (new Date().getTime() - this.startTime) - this.refreshTokenTime;
    }

    /**
     *
     * diff time is gt 0 we do fetchToken call if not we setTime to run the fethToken call after diff time
     */
    private static setTimeOutForRefreshToken(noodle: string, callback: () => void,failureCallback?: any) {
        try {
            let diffTime = this.getDiffTime();
            if (diffTime >= 0) {
                console.log('fetch token in setTimeOutForRefreshToken');
                this.fetchToken(this.baseUrl, noodle, callback, failureCallback);
            } else {
                console.log('setTimeOUt fired');
                if (diffTime < 0) {
                    diffTime = -(diffTime);
                }
                console.log('fetch tokein time:' + diffTime);
                this.refreshTokenTimeOutId = setTimeout(() => {                    
                    this.fetchToken(this.baseUrl, noodle, callback, failureCallback);
                }, diffTime);
                //@ts-ignore
                photoOrg.clientLogger && photoOrg.clientLogger.info({ description: 'setTimeout fired to trigger the refresh_access_token call', diffTimeMS: diffTime, Noodle: noodle });
            }
        } catch (e) {
            //@ts-ignore
            photoOrg.clientLogger && photoOrg.clientLogger.error({
                description: 'LIBRARY:OauthClient:setTimeOutForRefreshToken', errorCode: 'LIBRARY:OauthClient', err_desc: e, err_stack: e.stack
            });
        }
    }

}
//@ts-ignore
photoOrg.OauthClient = OauthClient;
